import {
  type HistoryGroup,
  getHistoryId,
  getOrderStatusUpdate,
  isHistoryGroup,
} from "@/features/history/utils";
import { useModals } from "@/features/modals/hooks";
import { type History, mapApiResponseToOrders, useDraftOrder } from "@repo/api";
import { LightMode, Text } from "@repo/skapa";
import { t } from "@repo/translation";
import { SimpleLabelField } from "@repo/ui";
import { trackAnalytics } from "@repo/utils";

import { HistoryListViewItem } from "./HistoryListViewItem";

interface Props {
  history: History | HistoryGroup;
}

const DraftOrderHistoryListItem = ({
  history,
}: {
  history: History | HistoryGroup;
}) => {
  const orderStatusUpdate = getOrderStatusUpdate(history);
  const showBody = !orderStatusUpdate;

  return (
    <div>
      {showBody && <Text textStyle="Body.S">{history.body}</Text>}
      <Text textStyle="Body.S">{history.note}</Text>
      {history.details?.order_number && (
        <SimpleLabelField
          small
          label={t("label.draft_order_id")}
          value={history.details.order_number}
          color={LightMode.colourTextAndIcon2}
        />
      )}
      {orderStatusUpdate && <Text>{orderStatusUpdate}</Text>}
    </div>
  );
};

export const DraftOrderHistoryItem = ({ history }: Props) => {
  const { pushModal } = useModals();

  const historyEntries = isHistoryGroup(history)
    ? history.prevEntries
    : undefined;
  const entryId = history.details?.order_number ?? getHistoryId(history);

  const { data } = useDraftOrder(entryId);

  const draftOrderData = mapApiResponseToOrders(data, entryId);

  const draftOrder = draftOrderData[0];
  return (
    <HistoryListViewItem
      history={history}
      control={draftOrder ? "navigational" : "default"}
      onClick={() => {
        if (draftOrder) {
          pushModal({
            type: "DRAFT_ORDER",
            draftOrderId: draftOrder.orderNo,
            historyEntries,
          });
        }
        trackAnalytics("draft_order_history");
      }}
      title={history.title}
      description={<DraftOrderHistoryListItem history={history} />}
    />
  );
};
